import { useTranslation } from 'react-i18next';
import React, { MouseEventHandler, useCallback, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { atom, useRecoilState } from 'recoil';

import { IconRcDownTriangle } from '@/assets';
import { isEthAddress, isOfficalAddress, request } from '@/utils';
import { useUserDescDict } from '@/hooks';
import storage from '@/utils/storage1';
import { localStorageEffect } from '@/utils/recoil';

import style from '../index.module.scss';

const recentSearchState = atom({
  key: 'recentSearch',
  default: storage.recentSearch || [],
  effects_UNSTABLE: [localStorageEffect('recentSearch')],
});

export const useRecentSearch = (enabled?: boolean) => {
  const { t } = useTranslation();

  const [recentSearch, setRecentSearch] = useRecoilState<string[]>(recentSearchState);
  const { data: dict, refetch: fetchDict } = useUserDescDict(
    recentSearch.filter(x => isEthAddress(x)),
    enabled,
  );
  const { data: officalDict, refetch: fetchOfficalDict } = useOfficalDict(
    recentSearch.filter(x => isOfficalAddress(x)),
    enabled,
  );
  const [isShowMore, setIsShowMore] = useState(false);
  const { data: hotList, refetch: fetchHot } = useHotList(enabled);

  const add = useCallback(
    (address: string) => {
      setRecentSearch(recentSearch => {
        let _recentSearch = recentSearch.slice();
        const index = _recentSearch.indexOf(address);
        if (index > -1) {
          _recentSearch.splice(index, 1);
        }
        _recentSearch.unshift(address);
        _recentSearch = _recentSearch.slice(0, 8);
        return _recentSearch;
      });
    },
    [setRecentSearch],
  );

  const remove = useCallback(
    (address: string) => {
      setRecentSearch(recentSearch => {
        let _recentSearch = recentSearch.slice();
        const index = _recentSearch.indexOf(address);
        if (index > -1) {
          _recentSearch.splice(index, 1);
        }
        return _recentSearch;
      });
    },
    [setRecentSearch],
  );

  const clear: MouseEventHandler<HTMLSpanElement> = useCallback(
    e => {
      e.preventDefault();
      e.stopPropagation();
      setRecentSearch([]);
    },
    [setRecentSearch],
  );

  const list = useMemo(() => {
    let result: { value: { desc: UserDesc; id: string } | OfficialAccount; _type: string }[] = [];
    recentSearch.forEach(address => {
      const isOffical = isOfficalAddress(address);
      const item = isOffical ? officalDict?.official_dict?.[address] : dict?.desc_dict?.[address];
      if (item) {
        result.push({
          value: isOffical
            ? (item as any)
            : {
                desc: item,
                id: address,
              },
          _type: 'recent',
        });
      }
    });

    return result;
  }, [recentSearch, dict, officalDict]);

  const data = useMemo(() => {
    const recentItem = list?.length
      ? {
          label: (
            <div className={style.recentSearchLabel}>
              <span>{t('hooks.14f1lkr')}</span>
              {list.length ? (
                <span className={style.recentSearchAction} onClick={clear}>
                  {t('hooks.c3ir72')}
                </span>
              ) : null}
            </div>
          ),
          extra:
            list.length > 3 && !isShowMore ? (
              <div
                className={style.recentSearchMore}
                onClick={e => {
                  e.stopPropagation();
                  e.preventDefault();
                  setIsShowMore(true);
                }}>
                {t('hooks.p5oxk')}
                <IconRcDownTriangle color="var(--light-sideinfo-color)" />
              </div>
            ) : null,
          children: isShowMore ? list : list.slice(0, 3),
          empty: <div className={style.recentSearchEmpty}>{t('hooks.1pz71vg')}</div>,
        }
      : null;

    const hotItem = hotList?.users?.length
      ? {
          label: (
            <div className={style.hotLabel}>
              <div>{t('hooks.2bmkxz')}</div>
              <div className={style.hotDesc}>{t('hooks.10418o8')}</div>
            </div>
          ),
          children: hotList.users.map(item => {
            return {
              value: item,
              _type: 'hot',
              isHideEnter: true,
            };
          }),
        }
      : null;
    return [recentItem, hotItem].filter(Boolean);
  }, [clear, hotList?.users, isShowMore, list, t]);

  const refetch = useCallback(() => {
    fetchDict();
    fetchOfficalDict();
    fetchHot();
  }, [fetchDict, fetchHot, fetchOfficalDict]);

  return {
    data,
    add,
    remove,
    clear,
    setIsShowMore,
    refetch,
  };
};

export const useOfficalDict = (ids: string[], enabled?: boolean) => {
  const url = '/official/dict';

  return useQuery(
    [url, ids.join(',')],
    () => {
      if (ids.length === 0) {
        return { official_dict: {} as Record<string, OfficialAccount> };
      }
      return request.get<{
        official_dict: Record<string, OfficialAccount>;
      }>(url, {
        ids: ids.join(','),
      });
    },
    {
      keepPreviousData: true,
      enabled,
    },
  );
};

export const useHotList = (enabled?: boolean) => {
  return useQuery(
    ['/user/hot_list'],
    () => {
      return request.get<{
        users: {
          id: string;
          desc: UserDesc;
        }[];
      }>('/user/hot_list');
    },
    {
      keepPreviousData: true,
      enabled,
    },
  );
};
